import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/pickleball-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "WK", title: "Weekly fixtures", desc: "Create recurring matchups across weeks, courts, divisions and sessions."},
    {code: "LD", title: "Ladder leagues", desc: "Let players challenge up, move down and track ranking movement over time."},
    {code: "DV", title: "Skill divisions", desc: "Separate players by beginner, intermediate, advanced or open levels."},
    {code: "SC", title: "Score reporting", desc: "Centralize weekly results so standings stay clear and accurate."},
    {code: "RK", title: "Player rankings", desc: "Track player or team rankings across a league season."},
    {code: "TB", title: "League standings", desc: "Publish league tables with points, wins, losses and results."},
];

const LEAGUE_TYPES = [
    {name: "Ladder Leagues", sub: "Challenges and ranking movement", to: "/ladder-league-generator/"},
    {name: "Round Robin Leagues", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "Pickleball Tournaments", sub: "Pool play and brackets", to: "/pickleball-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Manage recurring fixtures", desc: "Plan weekly matches across divisions, doubles teams and courts."},
    {title: "Track skill-based divisions", desc: "Keep 3.0, 3.5, 4.0 or open-level groups organized separately."},
    {title: "Publish standings clearly", desc: "Help players follow points, rankings, wins, losses and upcoming matches."},
];

const USE_CASES = [
    {title: "For Pickleball Clubs", desc: "Run weekly leagues, ladders, divisions and member competitions."},
    {title: "For League Organizers", desc: "Manage fixtures, standings and player rankings throughout the season."},
    {title: "For Social Groups", desc: "Keep recurring play organized without relying on group chats."},
    {title: "For Skill Divisions", desc: "Separate beginner, intermediate, advanced and open-level players."},
    {title: "For Ladder Competitions", desc: "Support challenges, ranking movement and flexible matchups."},
    {title: "For Seasonal Leagues", desc: "Run recurring competitions over weeks or months with clear standings."},
];

const COMPARE = [
    {need: "Weekly fixtures", manual: "Manual schedule updates", scorepal: "Structured recurring fixture management"},
    {need: "Ladder movement", manual: "Manual ranking changes", scorepal: "Clear player and team ranking movement"},
    {need: "Skill divisions", manual: "Separate tabs and duplicated formulas", scorepal: "Organized divisions and categories"},
    {need: "Score reporting", manual: "Messages, paper or manual entry", scorepal: "Centralized score tracking"},
    {need: "Player experience", manual: "Players ask organizers for tables and schedules", scorepal: "Fixtures, rankings and standings are easier to follow"},
];

const FAQS = [
    {
        q: 'What is pickleball league software?',
        a: <>Pickleball league software helps organizers create recurring fixtures, manage divisions, collect scores, publish standings and track player rankings across a season.</>,
    },
    {
        q: 'Can ScorePal manage pickleball ladder leagues?',
        a: <>Yes. ScorePal supports <Link to={"/ladder-league-generator/" as any} className="text-[#082F8A] underline font-semibold">ladder-style</Link> pickleball leagues where players or teams move up and down rankings over time.</>,
    },
    {
        q: 'Can ScorePal support skill divisions?',
        a: <>Yes. ScorePal can organize leagues by skill level, such as beginner, intermediate, advanced or open divisions.</>,
    },
    {
        q: 'Can ScorePal manage weekly pickleball fixtures?',
        a: <>Yes. ScorePal can manage recurring weekly fixtures, matchups, scores and standings.</>,
    },
    {
        q: 'Is this different from pickleball tournament software?',
        a: <>Yes. Pickleball league software focuses on recurring fixtures, ladder movement, standings and seasons. <Link to={"/pickleball-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Pickleball tournament software</Link> focuses on pool play, brackets, divisions and medal matches.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Pickleball League Software for Clubs & Organizers | ScorePal</title>
            <meta name="description" content="Run pickleball leagues with ScorePal. Manage weekly fixtures, divisions, ladders, player rankings, scores, standings and recurring pickleball competitions from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/pickleball-league-software/`} />
            <meta property="og:title" content="Pickleball League Software | ScorePal" />
            <meta property="og:description" content="Create pickleball league fixtures, manage divisions, track scores and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/pickleball-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/pickleball-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Pickleball League Software | ScorePal" />
            <meta name="twitter:description" content="Run pickleball leagues, ladders, standings and rankings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Pickleball League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Pickleball league software for managing weekly fixtures, divisions, ladders, scores, standings and player rankings.",
                "url": "https://scorepal.co/pickleball-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is pickleball league software?", "acceptedAnswer": {"@type": "Answer", "text": "Pickleball league software helps clubs and organizers create recurring fixtures, manage divisions, collect scores, publish standings and track player rankings across a season."}},
                    {"@type": "Question", "name": "Can ScorePal manage pickleball ladder leagues?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can support ladder-style pickleball leagues where players or teams move up and down rankings over time."}},
                    {"@type": "Question", "name": "Can ScorePal support skill divisions?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help organize leagues by skill level, such as beginner, intermediate, advanced or open divisions."}},
                    {"@type": "Question", "name": "Can ScorePal manage weekly pickleball fixtures?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. ScorePal can help manage recurring weekly fixtures, matchups, scores and standings."}},
                    {"@type": "Question", "name": "Is this different from pickleball tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Pickleball league software is focused on recurring fixtures, ladder movement, standings and seasons, while pickleball tournament software is focused on pool play, brackets, divisions and medal matches."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Pickleball League Software", item: "https://scorepal.co/pickleball-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Pickleball League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run pickleball leagues, ladders and weekly competitions without spreadsheets
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps pickleball clubs and organizers manage recurring fixtures, ladder leagues,
                                    skill divisions, weekly score reporting, player rankings and season standings from one platform.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/pickleball-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live league table card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live pickleball league table</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Keep players updated as results come in across divisions, teams and weekly rounds.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    {[
                                        {pos: 1, team: "Dink Masters", pts: "21 pts"},
                                        {pos: 2, team: "Kitchen Kings", pts: "18 pts"},
                                        {pos: 3, team: "Net Ninjas", pts: "15 pts"},
                                        {pos: 4, team: "Drop Shot Duo", pts: "12 pts"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[32px_1fr_auto] items-center gap-3 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm">{row.pts}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to manage a pickleball league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace spreadsheet standings, paper scorecards and group chat updates with software built for recurring pickleball competitions.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of pickleball league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Pickleball leagues come in different shapes. ScorePal helps clubs run structured seasons, flexible ladders and recurring social competitions.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Weekly competition + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Designed for weekly pickleball competition
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            A pickleball league needs more than a fixture list. Players need clear standings, weekly matchups,
                            court assignments, score reporting and a simple way to follow their progress.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "3.5 Doubles League — Week 6", r: "Active", badge: "live"},
                                {l: "Next match", r: "Dink Masters vs Net Ninjas"},
                                {l: "Division", r: "Intermediate"},
                                {l: "Completed", r: "34 / 48 matches"},
                                {l: "Top team", r: "Kitchen Kings", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for pickleball leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Spreadsheets become harder to manage as weekly results, substitutions, division tables and ranking changes start piling up.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for clubs, communities and recurring pickleball programs
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        ScorePal works for clubs running formal leagues, flexible ladders, weekly social competitions and skill-based divisions.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a one-day pickleball tournament?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is focused on recurring pickleball leagues. If you are running pool play, championship
                            brackets, consolation rounds or medal matches, see our pickleball tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/pickleball-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Pool play</li>
                            <li>Championship brackets</li>
                            <li>Consolation brackets</li>
                            <li>Skill divisions</li>
                            <li>Medal matches</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next pickleball league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Manage weekly fixtures, ladder movement, skill divisions, scores, rankings and standings with pickleball league software built for modern organizers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Weekly Fixtures", "Ladder Leagues", "Skill Divisions", "Rankings", "Standings", "Score Reporting"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Pickleball League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
